/**
 * Facebook Pixel utility functions
 * This file provides helper functions to track custom events with Facebook Pixel
 */

// Check if Facebook Pixel is loaded
const isFacebookPixelLoaded = () => {
  return typeof window !== 'undefined' && window.fbq;
};

/**
 * Track a custom event with Facebook Pixel
 * @param {string} eventName - The name of the event to track
 * @param {Object} parameters - Additional parameters for the event
 */
export const trackEvent = (eventName, parameters = {}) => {
  if (isFacebookPixelLoaded()) {
    window.fbq('track', eventName, parameters);
  } else {
    console.warn('Facebook Pixel not loaded');
  }
};

// Keep track of last events to prevent duplicates in strict mode
let lastTrackedPage = null;
let lastTrackedProduct = null;
let lastTrackTime = 0;

/**
 * Track page view (useful for SPA navigation)
 * @param {string} pageName - The name of the page
 */
export const trackPageView = (pageName) => {
  const now = Date.now();
  // Prevent duplicate page views within 100ms
  if (lastTrackedPage === pageName && now - lastTrackTime < 100) return;

  if (isFacebookPixelLoaded()) {
    window.fbq('track', 'PageView', { page_name: pageName });
    lastTrackedPage = pageName;
    lastTrackTime = now;
  }
};

/**
 * Track when a product is viewed
 * @param {Object} product - Product information
 */
export const trackViewContent = (product) => {
  const now = Date.now();
  // Prevent duplicate content views within 100ms for the same product
  if (lastTrackedProduct === product.id && now - lastTrackTime < 100) return;

  const parameters = {
    content_type: 'product',
    content_ids: [product.id],
    content_name: product.name,
    content_category: product.category,
    value: product.price,
    currency: 'TND' // Adjust based on your currency
  };

  trackEvent('ViewContent', parameters);
  lastTrackedProduct = product.id;
  lastTrackTime = now;
};

/**
 * Track when a product is added to cart
 * @param {Object} product - Product information
 * @param {number} quantity - Quantity added
 */
export const trackAddToCart = (product, quantity = 1) => {
  const parameters = {
    content_type: 'product',
    content_ids: [product.id],
    content_name: product.name,
    content_category: product.category,
    value: product.price * quantity,
    currency: 'TND',
    num_items: quantity
  };

  trackEvent('AddToCart', parameters);
};

/**
 * Track when a product is removed from cart
 * @param {Object} product - Product information
 * @param {number} quantity - Quantity removed
 */
export const trackRemoveFromCart = (product, quantity = 1) => {
  const parameters = {
    content_type: 'product',
    content_ids: [product.id],
    content_name: product.name,
    content_category: product.category,
    value: product.price * quantity,
    currency: 'TND',
    num_items: quantity
  };

  trackEvent('RemoveFromCart', parameters);
};

/**
 * Track when user initiates checkout
 * @param {Array} products - Array of products in cart
 * @param {number} totalValue - Total value of the cart
 */
export const trackInitiateCheckout = (products, totalValue) => {
  const contentIds = products.map(product => product.id);
  const parameters = {
    content_type: 'product',
    content_ids: contentIds,
    value: totalValue,
    currency: 'TND',
    num_items: products.length
  };

  trackEvent('InitiateCheckout', parameters);
};

/**
 * Track when a purchase is completed
 * @param {string} transactionId - Unique transaction ID
 * @param {Array} products - Array of products purchased
 * @param {number} totalValue - Total value of the purchase
 */
export const trackPurchase = (transactionId, products, totalValue) => {
  const contentIds = products.map(product => product.id);
  const parameters = {
    content_type: 'product',
    content_ids: contentIds,
    value: totalValue,
    currency: 'TND',
    num_items: products.length,
    transaction_id: transactionId
  };

  trackEvent('Purchase', parameters);
};

/**
 * Track when user searches for products
 * @param {string} searchTerm - The search term used
 * @param {number} resultsCount - Number of results returned
 */
export const trackSearch = (searchTerm, resultsCount = 0) => {
  const parameters = {
    search_string: searchTerm,
    content_category: 'product',
    num_items: resultsCount
  };

  trackEvent('Search', parameters);
};

/**
 * Track when user adds product to wishlist
 * @param {Object} product - Product information
 */
export const trackAddToWishlist = (product) => {
  const parameters = {
    content_type: 'product',
    content_ids: [product.id],
    content_name: product.name,
    content_category: product.category,
    value: product.price,
    currency: 'TND'
  };

  trackEvent('AddToWishlist', parameters);
};

/**
 * Track when user completes registration
 * @param {string} registrationMethod - How they registered (email, social, etc.)
 */
export const trackCompleteRegistration = (registrationMethod = 'email') => {
  const parameters = {
    registration_method: registrationMethod
  };

  trackEvent('CompleteRegistration', parameters);
};

/**
 * Track when user leads (contact form, newsletter signup, etc.)
 * @param {string} leadType - Type of lead generated
 */
export const trackLead = (leadType = 'contact_form') => {
  const parameters = {
    content_name: leadType
  };

  trackEvent('Lead', parameters);
};
